import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  ClipboardList, 
  FileText, 
  BookOpen,
  BarChart3,
  TrendingUp,
  PieChart,
  Menu,
  X,
  MapPin,
  ChevronDown,
  ChevronRight,
  Target,
  UserCheck,
  Building,
  Handshake
} from 'lucide-react';

const Navigation = ({ selectedLocation, onLocationChange, userRole = 'admin' }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [locations, setLocations] = useState(['All Locations']);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locationsData = await apiService.getLocations();
        const locationNames = ['All Locations', ...locationsData.map(loc => loc.name)];
        setLocations(locationNames);
      } catch (error) {
        console.error('Failed to load locations:', error);
        // Fallback to default locations
        setLocations([
          'All Locations',
          '55 Broadway',
          '60 Broad', 
          'East Austin',
          'Greenpoint',
          'Vinegar Hill',
          'Bushwick',
          'Center City',
          'Market Street'
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, []);

  // Logout function moved to Header component

  const [expandedReports, setExpandedReports] = useState(false);
  const [expandedCRM, setExpandedCRM] = useState(false);

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/',
      icon: LayoutDashboard
    },
    {
      name: 'CRM',
      icon: Target,
      isExpandable: true,
      subItems: [
        {
          name: 'Contacts',
          href: '/crm/contacts',
          icon: UserCheck
        },
        {
          name: 'Companies',
          href: '/crm/companies',
          icon: Building
        },
        {
          name: 'Deals',
          href: '/crm/deals',
          icon: Handshake
        }
      ]
    },
    {
      name: 'Office Management',
      href: '/offices',
      icon: Building2
    },
    {
      name: 'Member Directory',
      href: '/members',
      icon: Users
    },
    {
      name: 'Task Management',
      href: '/tasks',
      icon: ClipboardList
    },
    {
      name: 'Reporting',
      icon: BarChart3,
      isExpandable: true,
      subItems: [
        {
          name: 'Occupancy Reports',
          href: '/reporting',
          icon: PieChart
        },
        {
          name: 'Term Rate Analysis',
          href: '/term-analysis',
          icon: TrendingUp
        }
      ]
    },
    {
      name: 'Marketing Templates',
      href: '/templates',
      icon: FileText
    },
    {
      name: 'Employee Resources',
      href: '/resources',
      icon: BookOpen
    }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="bg-white shadow-lg"
        >
          {isCollapsed ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </Button>
      </div>

      {/* Sidebar */}
      <div className={`
        ${isCollapsed ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 
        transition-transform duration-300 ease-in-out flex flex-col
      `}>
        
        {/* Navigation Header - Simplified since main branding is now in page header */}
        <div className="p-4 border-b border-gray-100">
          <div className="text-center">
            <p className="text-sm font-medium text-slate-700">Navigation</p>
            <p className="text-xs text-gray-500">Operations Platform</p>
          </div>
        </div>

        {/* Location Selector */}
        <div className="p-4 border-b border-gray-100">
          <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2 block">
            Location
          </label>
          <Select value={selectedLocation} onValueChange={onLocationChange}>
            <SelectTrigger className="w-full">
              <MapPin className="w-4 h-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((loc) => (
                <SelectItem key={loc} value={loc}>
                  {loc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            
            if (item.isExpandable) {
              const hasActiveSubItem = item.subItems?.some(subItem => isActive(subItem.href));
              const isExpanded = item.name === 'Reporting' ? expandedReports : expandedCRM;
              const toggleExpanded = item.name === 'Reporting' 
                ? () => setExpandedReports(!expandedReports)
                : () => setExpandedCRM(!expandedCRM);
              
              return (
                <div key={item.name}>
                  <button
                    onClick={toggleExpanded}
                    className={`
                      w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200
                      ${hasActiveSubItem || isExpanded
                        ? 'bg-slate-50 text-slate-800 font-medium' 
                        : 'text-gray-600 hover:bg-gray-50 hover:text-slate-800'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className={`w-5 h-5 ${hasActiveSubItem || isExpanded ? 'text-slate-800' : 'text-gray-400'}`} />
                      <span className="font-medium">{item.name}</span>
                    </div>
                    {isExpanded ? 
                      <ChevronDown className="w-4 h-4 text-gray-400" /> : 
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    }
                  </button>
                  
                  {isExpanded && (
                    <div className="ml-6 mt-1 space-y-1">
                      {item.subItems?.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const subActive = isActive(subItem.href);
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            onClick={() => setIsCollapsed(false)}
                            className={`
                              flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200
                              ${subActive 
                                ? 'bg-slate-100 text-slate-800 border-l-2 border-slate-600 font-medium' 
                                : 'text-gray-600 hover:bg-gray-50 hover:text-slate-800'
                              }
                            `}
                          >
                            <SubIcon className={`w-4 h-4 ${subActive ? 'text-slate-800' : 'text-gray-400'}`} />
                            <span className="text-sm font-medium">{subItem.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsCollapsed(false)}
                className={`
                  flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200
                  ${active 
                    ? 'bg-slate-50 text-slate-800 border-l-4 border-slate-800 font-medium' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-slate-800'
                  }
                `}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-slate-800' : 'text-gray-400'}`} />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          {/* User Info moved to Header */}
          
          <Card className="p-4 bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200">
            <div className="text-center">
              <p className="text-xs text-gray-600 mb-1">Bond Collective Operations</p>
              <p className="text-xs font-semibold text-slate-700">Version 2.0</p>
              <div className="mt-2 text-xs text-gray-500">
                {selectedLocation !== 'All Locations' && (
                  <span className="font-medium">{selectedLocation}</span>
                )}
              </div>
            </div>
          </Card>
          
          {/* Settings and Logout moved to Header */}
        </div>
      </div>

      {/* Overlay for mobile */}
      {isCollapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsCollapsed(false)}
        />
      )}
    </>
  );
};

export default Navigation;